import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Download, ZoomIn, ZoomOut, RotateCw, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker - use unpkg CDN with dynamic version matching
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const DocumentPreviewModal = ({ 
  open, 
  onClose, 
  document: doc,
  title,
  downloadPath,
  presignPath  // Şub 2026 — İsteğe bağlı; verilmezse `downloadPath` üzerinden türetilir
}) => {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDirectUrl, setIsDirectUrl] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // PDF state
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);

  const isImage = doc?.file_type && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'].includes(doc.file_type.toLowerCase());
  const isPdf = doc?.file_type?.toLowerCase() === 'pdf' || doc?.file_type?.toLowerCase() === 'application/pdf';

  useEffect(() => {
    const loadDocument = async () => {
      if (!doc?.id || !open) return;
      
      setLoading(true);
      setError(null);
      setZoom(1);
      setRotation(0);
      setPdfPageNumber(1);
      setPdfNumPages(null);
      setIsDirectUrl(false);
      
      const effectiveDownload = downloadPath || `/documents/${doc.id}/download?preview=true`;
      // Şub 2026 — Hızlı yol: presigned S3 URL almayı dene, tarayıcı direkt S3'ten çeksin
      // download → download-url dönüşümü (query string'i koruyarak).
      let effectivePresign = presignPath;
      if (!effectivePresign) {
        try {
          const [pathPart, queryPart] = effectiveDownload.split('?');
          // /foo/{id}/download → /foo/{id}/download-url
          // /foo/{id}/file     → /foo/{id}/file-url
          let converted = pathPart;
          if (pathPart.endsWith('/download')) converted = pathPart + '-url';
          else if (pathPart.endsWith('/file')) converted = pathPart + '-url';
          if (converted !== pathPart) {
            effectivePresign = queryPart ? `${converted}?${queryPart}` : converted;
          }
        } catch { /* noop */ }
      }
      
      try {
        // 1) Presigned URL denemesi (S3 doğrudan)
        // Şub 27, 2026 — CORS-safe mode: sadece image için direct CDN URL;
        // PDF/DOC için CORS yapılana kadar blob fetch (aşağı düşer).
        if (effectivePresign) {
          try {
            const res = await api.get(effectivePresign);
            const url = res.data?.data?.url || res.data?.url;
            const ct = (res.data?.data?.content_type || '').toLowerCase();
            const corsSafe = isImage || (ct && ct.startsWith('image/'));
            if (res.data?.success && url && corsSafe) {
              setPreviewUrl(url);
              setIsDirectUrl(true);
              setLoading(false);
              return;
            }
          } catch { /* presign yoksa/başarısızsa blob'a düş */ }
        }

        // 2) Fallback: eski blob yolu
        const response = await api.get(effectiveDownload, { responseType: 'blob' });
        
        // Determine correct MIME type
        let mimeType = response.headers['content-type'] || 'application/octet-stream';
        const fileType = doc.file_type?.toLowerCase();
        
        // Map short types to MIME types
        const mimeTypeMap = {
          'pdf': 'application/pdf',
          'application/pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'image/jpeg': 'image/jpeg',
          'png': 'image/png',
          'image/png': 'image/png',
          'gif': 'image/gif',
          'image/gif': 'image/gif',
          'webp': 'image/webp',
          'image/webp': 'image/webp',
          'bmp': 'image/bmp',
          'image/bmp': 'image/bmp'
        };
        
        if (mimeTypeMap[fileType]) {
          mimeType = mimeTypeMap[fileType];
        }
        
        const blob = new Blob([response.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setIsDirectUrl(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Belge yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (previewUrl && !isDirectUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, open]);

  const handleClose = () => {
    if (previewUrl && !isDirectUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setIsDirectUrl(false);
    onClose();
  };

  const handleDownload = () => {
    if (previewUrl) {
      const a = window.document.createElement('a');
      a.href = previewUrl;
      a.download = doc?.original_name || doc?.file_name || 'document';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
    }
  };

  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <span className="truncate max-w-md">{title || doc?.file_name || 'Belge Önizleme'}</span>
              {isPdf && pdfNumPages && (
                <Badge variant="outline" className="ml-2">{pdfPageNumber} / {pdfNumPages}</Badge>
              )}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 py-2 border-b flex-shrink-0">
          {isPdf && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))}
                disabled={pdfPageNumber <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{pdfPageNumber} / {pdfNumPages || '?'}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages || p, p + 1))}
                disabled={pdfPageNumber >= (pdfNumPages || 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="border-l pl-4 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(s => Math.max(0.5, s - 0.25))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(s => Math.min(2, s + 0.25))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
          {isImage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
              <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}>
                <RotateCw className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded-lg flex items-start justify-center min-h-[400px] p-4">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-20">
              <p>{error}</p>
            </div>
          ) : isImage && previewUrl ? (
            <div className="overflow-auto w-full h-full flex items-center justify-center">
              <img
                src={previewUrl}
                alt={doc?.file_name || 'Preview'}
                className="max-w-full transition-transform duration-200"
                style={{ 
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center'
                }}
              />
            </div>
          ) : isPdf && previewUrl ? (
            isDirectUrl ? (
              /* Şub 2026 — S3 presigned URL için native iframe (CORS bypass, en hızlı) */
              <div className="w-full h-[70vh]">
                <iframe
                  src={previewUrl}
                  title={doc?.file_name || 'PDF'}
                  className="w-full h-full rounded shadow border-0 bg-white"
                />
              </div>
            ) : (
            <Document
              file={previewUrl}
              onLoadSuccess={onPdfLoadSuccess}
              onLoadError={(error) => {
                console.error('PDF load error:', error);
                setError('PDF yüklenemedi');
              }}
              loading={
                <div className="flex items-center gap-2 py-20">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span>PDF yükleniyor...</span>
                </div>
              }
              error={
                <div className="text-center py-10 text-red-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-red-400" />
                  <p>PDF yüklenemedi</p>
                  <Button className="mt-4" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    PDF'i İndir
                  </Button>
                </div>
              }
            >
              <Page
                pageNumber={pdfPageNumber}
                scale={zoom}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
            )
          ) : (
            <div className="text-center p-8 py-20">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">Bu dosya türü önizlenemez</p>
              <Button onClick={handleDownload} disabled={!previewUrl}>
                <Download className="h-4 w-4 mr-2" />
                Dosyayı İndir
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 border-t dark:border-slate-700 pt-4">
          <div className="flex justify-between items-center w-full">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Tür: {doc?.file_type?.toUpperCase() || 'Bilinmiyor'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Kapat
              </Button>
              <Button onClick={handleDownload} disabled={!previewUrl}>
                <Download className="h-4 w-4 mr-2" />
                İndir
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;
